/**
 * FreeGenerationService - Serviço para geração gratuita de conteúdo
 * 
 * Permite que usuários não autenticados testem o sistema 1 vez.
 * Usa fingerprint + localStorage para controle de limite.
 */

// Chave do localStorage
const STORAGE_KEY = 'hs_free_gen_used';
const BATCH_KEY = 'hs_free_gen_batch';

/**
 * Gera um fingerprint único do navegador
 * Combina várias características para criar um hash único
 */
const generateFingerprint = async () => {
  const components = [];
  
  // User Agent
  components.push(navigator.userAgent);
  
  // Idioma
  components.push(navigator.language);
  
  // Timezone
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
  
  // Resolução de tela
  components.push(`${window.width}x${window.height}x${window.colorDepth}`);
  
  // Plugins (simplificado)
  const plugins = Array.from(navigator.plugins || [])
    .map(p => p.name)
    .slice(0, 5)
    .join(',');
  components.push(plugins);
  
  // Canvas fingerprint
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Hello, Social! 🎨', 2, 2);
    components.push(canvas.toDataURL().slice(-50));
  } catch (e) {
    components.push('no-canvas');
  }
  
  // WebGL
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        components.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
        components.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
      }
    }
  } catch (e) {
    components.push('no-webgl');
  }
  
  // Hardware
  components.push(navigator.hardwareConcurrency || 'unknown');
  components.push(navigator.deviceMemory || 'unknown');
  
  // Gerar hash
  const str = components.join('|||');
  const hash = await hashString(str);
  
  return hash;
};

/**
 * Gera hash SHA-256 de uma string
 */
const hashString = async (str) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

/**
 * Verifica se já usou geração gratuita (verificação local rápida)
 */
const hasUsedFreeGeneration = () => {
  try {
    const used = localStorage.getItem(STORAGE_KEY);
    return used === 'true';
  } catch (e) {
    return false;
  }
};

/**
 * Marca como usado no localStorage
 */
const markAsUsed = (batchId) => {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
    localStorage.setItem(BATCH_KEY, batchId);
  } catch (e) {
    console.warn('Erro ao salvar no localStorage:', e);
  }
};

/**
 * Recupera batch_id salvo (para retomar polling)
 */
const getSavedBatchId = () => {
  try {
    return localStorage.getItem(BATCH_KEY);
  } catch (e) {
    return null;
  }
};

/**
 * Limpa dados salvos (para testes/debug)
 */
const clearSavedData = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(BATCH_KEY);
  } catch (e) {
    console.warn('Erro ao limpar localStorage:', e);
  }
};

// Base URL da API (sem autenticação)
const API_BASE = process.env.REACT_APP_API_URL || 'https://hellosocial-backend.bluemushroom-1364dc44.brazilsouth.azurecontainerapps.io';

/**
 * Cria uma geração gratuita
 */
const createFreeGeneration = async (username) => {
  const fingerprint = await generateFingerprint();
  
  const response = await fetch(`${API_BASE}/api/public/free-generation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username: username.replace('@', '').trim(),
      fingerprint
    })
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    const error = new Error(data.detail?.message || data.detail || 'Erro ao criar conteúdo');
    error.code = data.detail?.error || 'unknown_error';
    error.status = response.status;
    throw error;
  }
  
  // Marcar como usado e salvar batch_id
  if (data.data?.batch_id) {
    markAsUsed(data.data.batch_id);
  }
  
  return data;
};

/**
 * Verifica status do batch público
 */
const getBatchStatus = async (batchId) => {
  const response = await fetch(`${API_BASE}/api/public/batch/${batchId}/status`);
  
  const data = await response.json();
  
  if (!response.ok) {
    const error = new Error(data.detail || 'Erro ao buscar status');
    error.status = response.status;
    throw error;
  }
  
  return data;
};

/**
 * Verifica com o servidor se pode gerar
 */
const checkCanGenerate = async () => {
  // Verificação local primeiro (mais rápida)
  if (hasUsedFreeGeneration()) {
    return { can_generate: false, reason: 'Você já utilizou a geração gratuita.' };
  }
  
  try {
    const fingerprint = await generateFingerprint();
    const response = await fetch(`${API_BASE}/api/public/check-limit?fingerprint=${fingerprint}`);
    const data = await response.json();
    return data;
  } catch (e) {
    // Em caso de erro, permitir (melhor UX)
    return { can_generate: true };
  }
};

// Export do serviço
export const freeGenerationService = {
  generateFingerprint,
  hasUsedFreeGeneration,
  getSavedBatchId,
  clearSavedData,
  createFreeGeneration,
  getBatchStatus,
  checkCanGenerate
};

export default freeGenerationService;